import { css } from "@emotion/react";
import { Link } from "gatsby";
import React, { useState } from "react";
import { mediaQueries, pxToRem, weights, transition } from "../utils/css-utils";
import GridContainer from "./GridContainer";
import GridCol from "./GridCol";
import BlogSubNav from "./BlogSubNav";
import Button from "./Button";
import { colors } from "../utils/colors";
import { linkStyles, focusStyles } from "../utils/global-styles";

type HeaderProps = {
  blog: boolean
  simpleHeader? : boolean
};

const Header: React.FC<HeaderProps> = ({ blog, simpleHeader, ...props }) => {
  const data = {
    top: [
      {
        text: "Support",
        href: "https://console.upsun.com/-/users/:user/tickets/open",
      },
      {
        text: "Docs",
        href: "https://docs.upsun.com/",
      },
      {
        text: "Community",
        icon: (
          <svg
            width="16"
            height="14"
            viewBox="0 0 16 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              id="Vector"
              d="M13.5535 1.95307C12.5027 1.47234 11.3928 1.13004 10.2526 0.9375C10.0966 1.21688 9.95561 1.5038 9.82976 1.79702C8.61535 1.61455 7.38081 1.61455 6.16641 1.79702C6.04056 1.5038 5.89961 1.21688 5.74357 0.9375C4.60341 1.13256 3.49219 1.47486 2.44013 1.95559C0.351096 5.04635 -0.215207 8.06034 0.0679444 11.0315C1.29116 11.9351 2.66036 12.6235 4.11639 13.064C4.44358 12.6235 4.73429 12.1553 4.98346 11.6658C4.51028 11.4896 4.05346 11.2707 3.61804 11.0139C3.73256 10.9309 3.84456 10.8453 3.95279 10.7622C6.51626 11.9678 9.48369 11.9678 12.0472 10.7622C12.1566 10.8516 12.2686 10.9372 12.3819 11.0139C11.9465 11.2719 11.4884 11.4896 11.014 11.6671C11.2631 12.1566 11.5526 12.6235 11.881 13.064C13.3383 12.6248 14.7088 11.9376 15.932 11.0328C16.2642 7.58716 15.3644 4.60086 13.5535 1.95307ZM5.34212 9.20428C4.55307 9.20428 3.90119 8.48822 3.90119 7.6073C3.90119 6.72638 4.53042 6.00403 5.3396 6.00403C6.14879 6.00403 6.79563 6.72638 6.78179 7.6073C6.76795 8.48822 6.14627 9.20428 5.34212 9.20428ZM10.6578 9.20428C9.86752 9.20428 9.21815 8.48822 9.21815 7.6073C9.21815 6.72638 9.84738 6.00403 10.6578 6.00403C11.4683 6.00403 12.1101 6.72638 12.0962 7.6073C12.0824 8.48822 11.462 9.20428 10.6578 9.20428Z"
              fill="white"
            />
          </svg>
        ),
        href: "https://discord.gg/PkMc2pVCDV",
      },
    ],
    links: [
      {
        text: "Features",
        href: "/features/"
      },
      {
        text: "Pricing",
        href: "/pricing/",
      },
      {
        text: "Blog",
        href: "/blog/",
      },
      {
        text: "About us",
        href: "/about-us/",
      }
    ],
    ctas: [
      {
        text: "Login",
        href: "https://auth.upsun.com/",
      },
      {
        text: "Free trial",
        href: "https://auth.upsun.com/register",
      },
    ],
  };

  const linksStyles = css`
    color: #fff;
    text-decoration: none;
    font-weight: ${weights.bold};
    padding: ${pxToRem(20)};
    padding-left: 0;
    border-bottom: ${pxToRem(1)} solid ${colors.black600};

    ${mediaQueries.xl} {
      padding: 0;
      text-align: center;
      border-bottom: none;
    }
  `;

  const containerOne = (isOpen: Boolean) => css`
    height: ${isOpen ? "100vh" : "0vh"};
    min-height: ${isOpen ? "100vh" : "0vh"};
    overflow: hidden;
    transition: all 0.25s ease-in-out;
    margin-left: ${pxToRem(-20)};
    margin-right: ${pxToRem(-20)};
    padding-left: ${pxToRem(20)};
    padding-right: ${pxToRem(20)};

    &::before {
      content: "";
      position: absolute;
      left: ${pxToRem(-20)};
      right: ${pxToRem(-20)};
      height: ${pxToRem(160)};
      background: rgba(24, 30, 33, 1);
      z-index: -1;
      display: ${isOpen ? "block" : "none"};
    }
    
    ${mediaQueries.md} {
      margin-left: ${pxToRem(-40)};
      margin-right: ${pxToRem(-40)};
      padding-left: ${pxToRem(40)};
      padding-right: ${pxToRem(40)};
      
      &::before {
        left: ${pxToRem(-40)};
        right: ${pxToRem(-40)};
        height: ${pxToRem(192)};
      }
    }

    ${mediaQueries.xl} {
      height: auto;
      min-height: auto;
      display: flex;
      align-items: center;
      padding: 0;
      margin: unset;
      overflow: visible;

      &::before {
        display: none;
      }
    }
  `;

  const ctaContainerStyles = css`
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(16)};
    padding-top: 32px;
    padding-bottom: 32px;

    ${mediaQueries.xl} {
      flex-direction: row;
      order: 2;
      padding-top: 0px;
      padding-bottom: 0px;
    }
  `;

  const containerStyles = css`
    font-family: "Space Grotesk", sans-serif;
    display: flex;
    flex-direction: column;
    position: sticky;
    top: 0;
    background-color: #0C0F10;
    z-index: 999;
    padding-top: ${pxToRem(20)};
    padding-bottom: ${pxToRem(20)};
    font-size: ${pxToRem(16)};

    ${mediaQueries.xl} {
      font-size: ${pxToRem(18)};
      padding-top: ${pxToRem(24)};
      padding-bottom: ${pxToRem(24)};
    }
  `;

  const containerRow = css`
    ${mediaQueries.xl} {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;
    }
  `;

  const linksContainerStyles = css`
    display: flex;
    flex-direction: column;

    a {
      transition: ${transition()};

      &:hover {
        color: ${colors.violet300};
      }
      
      &:active {
        color: ${colors.violet400};
      }
      
      &:focus-visible {
        ${focusStyles}
      }
    }

    ${mediaQueries.xl} {
      flex-direction: row;
      order: 1;
      gap: 48px;
      margin-right: 48px;
    }
    
    ${mediaQueries.xxl} {
      gap: 64px;
      margin-right: 64px;
    }
`;

const topLinkMobileStyles = css`
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(16)};
    font-size: ${pxToRem(14)};
    margin-top: 16px;

    svg {
      margin-right: ${pxToRem(8)};
      position: relative;
      top: ${pxToRem(2)};
    }

    a {
      text-decoration: none;
      color: rgba(180, 186, 189, 1);
      transition: ${transition()};
      
      &:hover {
        color: ${colors.violet300};
      }
      
      &:active {
        color: ${colors.violet400};
      }
    }

    ${mediaQueries.xl} {
      display: none;
    }
  `;

  const topLinkStyles = css`
    display: none;

    svg {
      margin-right: ${pxToRem(8)};
      position: relative;
      top: ${pxToRem(2)};
    }

    ${mediaQueries.xl} {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      flex: 0 0 100%;
      font-size: ${pxToRem(14)};
      gap: ${pxToRem(40)};
      margin-bottom: ${pxToRem(16)};

      a {
        text-decoration: none;
        color: rgba(180, 186, 189, 1);
        transition: ${transition()};
        
        &:hover {
          color: ${colors.violet300};
        }
        
        &:active {
          color: ${colors.violet400};
        }
        
        &:focus-visible {
          ${focusStyles}
        }
      }
    }
  `;

  const [isOpen, setIsOpen] = useState(false);

  const logoOpenCloseStyles = css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    ${simpleHeader && `margin-top: 18px`};

    ${mediaQueries.md} {
      ${simpleHeader && `margin-top: 36px`};
    }

    ${mediaQueries.xl} {
      margin-bottom: 0;
      ${simpleHeader && `margin-top: 0px`};
    }
  `;

  const openCloseButton = css`
    cursor: pointer;
    
    ${mediaQueries.xl} {
      display: none;
    }
  `;

  const mobileLogoStyles = css`
    position: relative;
    height: ${pxToRem(24)};
    top: 4px;
    object-fit: contain;
    object-position: left;

    ${mediaQueries.md} {
      height: ${pxToRem(28)};
    }

    ${mediaQueries.xl} {
      display: none;
    }
  `;

  const desktopLogoStyles = css`
    display: none;

    ${mediaQueries.xl} {
      display: block;
      object-fit: contain;
      height: 62px;
    }
  `;

  const logoContainer = css`
    display: flex;
    align-items: flex-start;
    
    a {
      &:focus-visible {
        ${focusStyles}
      }
    }
  `;

  return (
    <GridContainer css={containerStyles}>
      <GridCol colStart={1} colEnd={13} css={containerRow}>
        {!simpleHeader && <div css={topLinkStyles}>
          {data.top.map((link) => {
            return (
              <a href={link.href} key={link.href}>
                {link.icon}
                {link.text}
              </a>
            );
          })}
        </div>}
        <div css={logoOpenCloseStyles}>
          <div css={logoContainer}>
            <Link to="/">
              <img src="/logos/logo_horizontal.svg" alt="Homepage" css={mobileLogoStyles} />
              <img src="/logos/logo_vertical.svg" alt="Homepage" css={desktopLogoStyles} />
            </Link>
          </div>
          {!simpleHeader && <div css={openCloseButton}>
            {isOpen ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => setIsOpen(false)}
              >
                <g id="close">
                  <path
                    id="Icon"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M17.7046 6.29537C17.3108 5.90154 16.6723 5.90154 16.2785 6.29537L12 10.5738L7.72153 6.29537C7.3277 5.90154 6.68919 5.90154 6.29537 6.29537C5.90154 6.68919 5.90154 7.3277 6.29537 7.72153L10.5738 12L6.29537 16.2785C5.90154 16.6723 5.90154 17.3108 6.29537 17.7046C6.68919 18.0985 7.3277 18.0985 7.72153 17.7046L12 13.4262L16.2785 17.7046C16.6723 18.0985 17.3108 18.0985 17.7046 17.7046C18.0985 17.3108 18.0985 16.6723 17.7046 16.2785L13.4262 12L17.7046 7.72153C18.0985 7.3277 18.0985 6.68919 17.7046 6.29537Z"
                    fill="white"
                  />
                </g>
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => setIsOpen(true)}
              >
                <g id="hamburger">
                  <path
                    id="Icon"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0 7C0 7.55229 0.447716 8 1 8L23 8C23.5523 8 24 7.55228 24 7C24 6.44772 23.5523 6 23 6H1C0.447715 6 0 6.44772 0 7ZM24 17C24 16.4477 23.5523 16 23 16L1 16C0.447715 16 0 16.4477 0 17C0 17.5523 0.447716 18 1 18L23 18C23.5523 18 24 17.5523 24 17Z"
                    fill="white"
                  />
                </g>
              </svg>
            )}
          </div>}
        </div>
        {!simpleHeader && <div css={containerOne(isOpen)}>
          <div css={ctaContainerStyles}>
            {data.ctas.map((cta, index) => {
              return (
                <Button href={cta.href} text={cta.text} key={cta.href} level={index ? 'primary' : 'secondary'}/>
              );
            })}
          </div>
          <div css={linksContainerStyles}>
            {data.links.map((link) => {
              return (
                <Link to={link.href} css={linksStyles} key={link.href}>
                  {link.text}
                </Link>
              );
            })}
          </div>
          <div css={topLinkMobileStyles}>
            {data.top.map((link) => {
              return (
                <a href={link.href} key={link.href}>
                  {link.icon}
                  {link.text}
                </a>
              );
            })}
          </div>
        </div>}
      </GridCol>
      {blog && (
        <GridCol colStart={1} colEnd={13}>
          <BlogSubNav hasScrolledBelowHeader={false} />
        </GridCol>
      )}
    </GridContainer>
  );
};

export default Header;
